import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { colors } from "../styles/colors";

const ServiceSection = styled.section`
  padding: 60px 20px 120px 20px; /* Reduced top padding from 120px to 60px to fix the large space */
  background-color: ${colors.secondary}; /* Dark Green background from branding kit */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Subtitle = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.15rem;          /* Increased from 0.85rem to match About section */
  font-weight: 700;            /* Made it bold */
  text-transform: uppercase;
  letter-spacing: 0.25em;      /* Elegant, wide premium character layout */
  color: ${colors.primary};
  display: block;
  text-align: center;
  margin-bottom: 15px;         /* Adjusted breathing room below it */
`;

const MainHeading = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 2.8rem;
  text-align: center;
  margin-bottom: 60px;
  color: ${colors.textLight};
  font-weight: 500;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid ${(props) => props.isOpen ? colors.primary : "rgba(255, 255, 255, 0.1)"};
  border-radius: 0px;
  overflow: hidden;
  transition: border-color 0.4s ease;
  height: fit-content;
  
  &:hover {
    border-color: ${colors.primary};
  }
`;

const CardHeader = styled.div`
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const CardTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.textLight};
  margin: 0;
`;

const ExpandIcon = styled(motion.span)`
  font-size: 0.8rem;
  color: ${colors.primary};
`;

const CardContent = styled(motion.div)`
  overflow: hidden;
`;

const CardDesc = styled.p`
  font-family: 'Montserrat', sans-serif;
  padding: 0 30px 30px 30px;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
`;

const services = [
  { 
    title: "Property Management", 
    desc: "Excellence in every detail. From STR/LTR residential to commercial properties, we handle tenant relations and maintenance to ensure your investments stay in peak condition." 
  },
  { 
    title: "Real Estate", 
    desc: "Making property transactions seamless. Our expert team guides you through buying, selling, or renting, ensuring the best deals and smooth experiences every step of the way." 
  },
  { 
    title: "Business Consultancy", 
    desc: "Turn potential into performance. With strategic insights and actionable advice, we help startups and established businesses optimize operations, drive growth, and achieve lasting success." 
  },
  { 
    title: "E-commerce Solutions", 
    desc: "Navigating global digital marketplaces. We design, optimize, and scale end-to-end e-commerce operations—from digital store architecture to supply chain logistics—ensuring sustainable digital retail growth." 
  },
  { 
    title: "Restaurant Management", 
    desc: "Efficiency meets profitability. We streamline operations, enhance customer satisfaction, and implement strategies that boost revenue for your restaurant business." 
  },
  { 
    title: "Medical Billing", 
    desc: "Accurate, reliable, and timely. We manage claims, payments, and reporting to maximize revenue and reduce errors, keeping your healthcare practice running smoothly." 
  },
  { 
    title: "Marketing", 
    desc: "Connect, engage, grow. Our marketing solutions combine creativity with data-driven strategies to elevate your brand, attract leads, and expand your market reach." 
  },
  { 
    title: "Renovation and Fitouts", 
    desc: "Spaces that inspire. From concept to completion, we deliver high-quality renovations and fit-outs that blend functionality with style, tailored to your vision." 
  },
  { 
    title: "Mortgage & Loan Solutions", 
    desc: "We help clients navigate mortgages and home financing smoothly, providing personalized guidance at every step. Our goal is to make the process clear, stress-free, and tailored to your unique financial needs." 
  }
];

export default function Services() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <ServiceSection id="services">
      <Container>
        <Subtitle>Our Expertise</Subtitle>
        <MainHeading>Bespoke Global Services</MainHeading>

        <CardGrid>
          {services.map((service, index) => (
            <Card 
              key={index} 
              isOpen={openIndex === index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
            >
              <CardHeader onClick={() => setOpenIndex(openIndex === index ? null : index)}>
                <CardTitle>{service.title}</CardTitle>
                <ExpandIcon animate={{ rotate: openIndex === index ? 180 : 0 }}>
                  ▼
                </ExpandIcon>
              </CardHeader>

              <AnimatePresence mode="wait">
                {openIndex === index && (
                  <CardContent
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CardDesc>{service.desc}</CardDesc>
                  </CardContent>
                )}
              </AnimatePresence>
            </Card>
          ))}
        </CardGrid>
      </Container>
    </ServiceSection>
  );
}